import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';

import { Button, Popup, PopupSection, Text } from '@flixbus/honeycomb-react';

import { FlowController } from './FlowController';
import { FLOW_MODAL_TEXT } from './texts';

import styles from './styles.module.css';

/**
 * Modal flow controller component.
 *
 * Owns one FlowController per modal session: a fresh controller is created
 * each time `isOpen` transitions from false → true (so reopening always
 * starts a clean session seeded with `initialData`).
 *
 * The shell owns the Cancel / Back / Continue / Confirm buttons. Steps just
 * render their inputs and call `onDraftChange(values)` on each local form
 * change. The Continue button is enabled/disabled based on the current
 * node's `validate(mergedData)` rule (defaults to enabled if no rule set).
 *
 * `isOpen` defaults to `true` so the modal can be plugged into the global
 * ActiveElements registry, where visibility is controlled by mounting/
 * unmounting rather than a prop.
 *
 * @param {import('./flowTypes').FlowModalProps} props
 */
const FlowModalImpl = ({
	isOpen = true,
	flowConfig,
	initialData,
	onSubmit,
	onClose,
	bannerSlot = null,
	isConfirmDisabled = false,
	isConfirmLoading = false,
	onNavigate,
}) => {
	const controllerRef = useRef(null);
	const wasOpenRef = useRef(false);

	if (isOpen && !wasOpenRef.current) {
		controllerRef.current = new FlowController(flowConfig, initialData);
	}

	useEffect(() => {
		wasOpenRef.current = isOpen;
	}, [isOpen]);

	if (!isOpen || !controllerRef.current) {
		return null;
	}

	const controller = controllerRef.current;
	const node = controller.currentNode;

	if (!node || !node.component) {
		throw new Error(`FlowModal: current node "${controller.currentNodeId}" has no component`);
	}

	const NodeComponent = node.component;

	const handleConfirm = () =>
		onSubmit(controller.getData(), {
			currentNodeId: controller.currentNodeId,
			previousNodeId: controller.history[controller.history.length - 1] ?? null,
			history: [...controller.history],
		});

	const handleContinue = () => {
		const fromNodeId = controller.currentNodeId;
		controller.commitStep();
		onNavigate?.({ direction: 'forward', fromNodeId, toNodeId: controller.currentNodeId });
	};
	const handleBack = () => {
		const fromNodeId = controller.currentNodeId;
		controller.goBack();
		onNavigate?.({ direction: 'back', fromNodeId, toNodeId: controller.currentNodeId });
	};

	const commonProps = {
		data: controller.getData(),
		node,
		onBack: handleBack,
		canGoBack: controller.canGoBack,
		onCancel: onClose,
		onDraftChange: (values) => controller.updateDraft(controller.currentNodeId, values),
	};

	const nodeProps = controller.isSummary
		? {
				...commonProps,
				onConfirm: handleConfirm,
				flowConfig,
				previousNodeId: controller.history[controller.history.length - 1] ?? null,
			}
		: {
				...commonProps,
				// Back-compat: legacy nodes still submit their own values via a
				// local button. The shell's Continue path uses the pending draft.
				onSubmitStep: (values) => {
					const fromNodeId = controller.currentNodeId;
					controller.commitStep(values);
					onNavigate?.({
						direction: 'forward',
						fromNodeId,
						toNodeId: controller.currentNodeId,
					});
				},
			};

	const subtitle = node.meta?.subtitle;
	const primaryLabel = controller.isSummary
		? FLOW_MODAL_TEXT.confirm
		: node.meta?.primaryLabel || FLOW_MODAL_TEXT.continue;

	// Two overflow modes:
	// - Input steps (default): body/content `overflow: visible` so HC's
	//   inline Autocomplete listbox can escape the popup without clipping.
	// - Scroll-owning steps (summary + anything else that opts in): body
	//   keeps HC's default clip and content owns a vertical scroll, so
	//   long content stays inside the popup and buttons remain reachable.
	//   Steps opt in via `node.meta.scrollableContent === true`.
	const shellClass = node.meta?.scrollableContent ? styles.shellScrollable : styles.shell;

	return (
		<Popup id="flow-engine-modal" active onOverlayClick={onClose} extraClasses={shellClass}>
			<PopupSection type="title">{node.title}</PopupSection>
			<PopupSection
				type="content"
				extraClasses={classNames({ [styles.contentOverflowVisible]: shellClass === styles.shell })}
			>
				{subtitle && <Text>{subtitle}</Text>}
				<NodeComponent {...nodeProps} />
				{bannerSlot}
			</PopupSection>
			<PopupSection type="actions">
				{!controller.canGoBack ? (
					<Button type="button" onClick={onClose} data-testid="flow-modal-cancel">
						{FLOW_MODAL_TEXT.cancel}
					</Button>
				) : (
					<Button type="button" onClick={handleBack} data-testid="flow-modal-back">
						{FLOW_MODAL_TEXT.back}
					</Button>
				)}
				{controller.isSummary ? (
					<Button
						type="button"
						appearance="primary"
						disabled={isConfirmDisabled}
						loading={isConfirmLoading}
						onClick={handleConfirm}
						data-testid="flow-modal-confirm"
					>
						{primaryLabel}
					</Button>
				) : (
					<Button
						type="button"
						appearance="primary"
						disabled={!controller.isCurrentStepValid}
						onClick={handleContinue}
						data-testid="flow-modal-continue"
					>
						{primaryLabel}
					</Button>
				)}
			</PopupSection>
		</Popup>
	);
};

export const FlowModal = observer(FlowModalImpl);
